// src/components/ro/ROStatusTimeline.js
import React, { useMemo } from "react";
import { formatROLabel } from "../../utils/roFormat";
import { safeString } from "../../utils/roModel";

function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatWhen(iso) {
  const d = safeDate(iso);
  if (!d) return "—";
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function prettyValue(v) {
  const raw = safeString(v).trim();
  if (!raw || raw === "—") return "—";
  return raw
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function getEventTone(type) {
  const t = safeString(type).toLowerCase();

  if (t.includes("complete") || t.includes("archived")) return "success";
  if (t.includes("parts")) return "warning";
  if (t.includes("assign") || t.includes("tech")) return "blue";
  if (t.includes("created") || t.includes("imported")) return "neutral";
  if (t.includes("blocked") || t.includes("ignored")) return "danger";
  if (t.includes("edited")) return "subtle";
  if (t.includes("note")) return "note";

  return "neutral";
}

function toneStyles(tone) {
  const map = {
    success: { rail: "#16a34a", bg: "#ffffff" },
    warning: { rail: "#f59e0b", bg: "#ffffff" },
    blue: { rail: "#2563eb", bg: "#ffffff" },
    danger: { rail: "#dc2626", bg: "#fffafa" },
    note: { rail: "#64748b", bg: "#ffffff" },
    subtle: { rail: "#cbd5e1", bg: "#ffffff" },
    neutral: { rail: "#94a3b8", bg: "#ffffff" },
  };

  return map[tone] || map.neutral;
}

function itemCardStyle(tone) {
  const styles = toneStyles(tone);

  return {
    position: "relative",
    border: "1px solid #e5e7eb",
    borderLeft: `3px solid ${styles.rail}`,
    borderRadius: 10,
    padding: "8px 10px",
    background: styles.bg,
  };
}

function emptyStateStyle() {
  return {
    padding: 12,
    border: "1px dashed #d1d5db",
    borderRadius: 10,
    background: "#fafafa",
    color: "#6b7280",
    fontSize: 12,
  };
}

function getActorName(evt, fallback = "—") {
  const meta = evt?.meta && typeof evt.meta === "object" ? evt.meta : {};

  return (
    safeString(evt?.actor).trim() ||
    safeString(evt?.byName).trim() ||
    safeString(evt?.actorName).trim() ||
    safeString(meta?.byName).trim() ||
    safeString(meta?.actorName).trim() ||
    safeString(meta?.userName).trim() ||
    fallback
  );
}

function prettyEventType(rawType) {
  const t = safeString(rawType).trim();
  if (!t) return "Event";

  const key = t.toUpperCase();

  const map = {
    RO_CREATED: "RO Created",
    RO_IMPORTED: "RO Imported",
    RO_EDITED: "RO Edited",

    RO_ASSIGNED: "Tech Assigned",
    ASSIGNED: "Tech Assigned",
    TECH_ASSIGNED: "Tech Assigned",

    RO_UNASSIGNED: "Tech Unassigned",
    UNASSIGNED: "Tech Unassigned",
    TECH_UNASSIGNED: "Tech Unassigned",

    RO_STATUS_CHANGED: "Status Changed",
    RO_TRANSPORT_CHANGED: "Transport Changed",
    RO_WORK_MIX_CHANGED: "Work Type Changed",

    RO_PARTS_STATUS_CHANGED: "Parts Status Changed",
    RO_PARTS_ETA_CHANGED: "Parts ETA Changed",
    RO_PARTS_REQUESTED: "Parts Requested",
    RO_PARTS_ACTION_STATUS_CHANGED: "Parts Action Changed",
    PARTS_REQUESTED: "Parts Requested",
    PARTS_ACTION_UPDATED: "Parts Action Updated",

    RO_FLAGGED_HOURS_SET: "Flagged Hours Set",

    RO_NOTE_ADDED: "Note Added",
    NOTE_ADDED: "Note Added",
    RO_MESSAGE_RETRACTED: "Message Withdrawn",
    RO_ATTACHMENT_RETRACTED: "Attachment Withdrawn",

    RO_WORK_STARTED: "Work Started",
    WORK_STARTED: "Work Started",

    RO_WORK_ENDED: "Work Stopped",
    WORK_STOPPED: "Work Stopped",
    WORK_ENDED: "Work Stopped",

    RO_BREAK_STARTED: "Break Started",
    BREAK_STARTED: "Break Started",

    RO_BREAK_ENDED: "Break Ended",
    BREAK_ENDED: "Break Ended",

    RO_WORK_COMPLETED: "Work Completed",
    WORK_COMPLETED: "Work Completed",
    RO_COMPLETED: "Work Completed",
    COMPLETED: "Completed",

    RO_ARCHIVED: "Archived",
    ARCHIVED: "Archived",

    RO_FLAG_SET: "Flag Set",
    RO_FLAG_CLEARED: "Flag Cleared",

    RO_CUSTOMER_CONTACT_STATUS_CHANGED: "Customer Contact Updated",
    RO_CUSTOMER_CONTACT_STATUS_CLEARED: "Customer Contact Cleared",
    CUSTOMER_CONTACTED: "Customer Contacted",
    VEHICLE_GONE: "Vehicle Gone",

    RO_COMPLETE_BLOCKED_FLAGGED_HOURS_REQUIRED: "Complete Blocked",
    RO_BREAK_START_IGNORED_NO_ACTIVE_WORK: "Break Start Ignored",
    RO_BREAK_START_IGNORED_ALREADY_ON_BREAK: "Break Start Ignored",
    RO_BREAK_END_IGNORED_NOT_ON_BREAK: "Break End Ignored",
    RO_WORK_END_IGNORED_NO_ACTIVE_WORK: "Work Stop Ignored",
  };

  if (map[key]) return map[key];

  return prettyValue(t);
}

function rawEventKey(evt) {
  return safeString(evt?.type || evt?.eventType || "").trim().toUpperCase();
}

function asFiniteNumber(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getMetaNumber(meta, keys = []) {
  const m = meta && typeof meta === "object" ? meta : {};

  for (const key of keys) {
    const n = asFiniteNumber(m?.[key]);
    if (n != null && n > 0) return n;
  }

  const changedFields = Array.isArray(m?.changedFields) ? m.changedFields : [];

  for (const field of changedFields) {
    const fieldKey = safeString(field?.key || field?.field || field?.label || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");

    if (
      fieldKey === "flaggedhours" ||
      fieldKey === "flagged_hours" ||
      fieldKey === "completedhours" ||
      fieldKey === "completed_hours" ||
      fieldKey === "hoursflagged" ||
      fieldKey === "hourscompleted"
    ) {
      const n = asFiniteNumber(field?.to ?? field?.value ?? field?.newValue);
      if (n != null && n > 0) return n;
    }
  }

  return null;
}

function shouldHideRawChangedFields(rawType) {
  const key = safeString(rawType).trim().toUpperCase();

  return (
    key === "RO_ASSIGNED" ||
    key === "TECH_ASSIGNED" ||
    key === "ASSIGNED" ||
    key === "RO_UNASSIGNED" ||
    key === "TECH_UNASSIGNED" ||
    key === "UNASSIGNED" ||
    key === "RO_WORK_STARTED" ||
    key === "WORK_STARTED" ||
    key === "RO_WORK_ENDED" ||
    key === "WORK_STOPPED" ||
    key === "WORK_ENDED" ||
    key === "RO_WORK_COMPLETED" ||
    key === "WORK_COMPLETED" ||
    key === "RO_COMPLETED" ||
    key === "COMPLETED"
  );
}

function getHistoryTimestamp(evt) {
  return evt?.ts || evt?.at || evt?.timestamp || evt?.createdAt;
}

function getEventDedupeKey(evt) {
  const ts = safeString(evt?.timestamp || "").trim();
  const type = safeString(evt?.type || "").trim();
  const note = safeString(evt?.note || "").trim();
  const actor = safeString(evt?.actor || "").trim();

  return `${ts}|${type}|${actor}|${note}`;
}

function buildSystemTimeline(ro, getTechName) {
  if (!ro) return [];

  const t = ro.timestamps || {};
  const items = [];

  const push = (timestamp, actor, type, note) => {
    if (!safeDate(timestamp)) return;
    items.push({
      timestamp,
      actor: actor || "—",
      type: type || "Event",
      note: note || "",
      changedFields: [],
      source: "system",
    });
  };

  push(
    t.createdAt || ro.createdAt,
    ro.createdByName || ro.advisorName || "System",
    "RO Created",
    `${formatROLabel(ro)} created.`
  );

  push(
    t.assignedAt || ro.assignedAt,
    "Dispatch",
    "Tech Assigned",
    `Assigned to ${
      ro.assignedTechName || getTechName?.(ro.assignedTechId) || "—"
    }.`
  );

  push(
    t.workStartAt || ro.workStartAt,
    ro.assignedTechName || getTechName?.(ro.assignedTechId) || "—",
    "Work Started",
    "Work started."
  );

  push(
    t.breakStartAt || ro.breakStartAt,
    ro.assignedTechName || getTechName?.(ro.assignedTechId) || "—",
    "Break Started",
    "Break started."
  );

  push(
    t.breakEndAt || ro.breakEndAt,
    ro.assignedTechName || getTechName?.(ro.assignedTechId) || "—",
    "Break Ended",
    "Break ended."
  );

  push(
    t.workEndAt || ro.workEndAt,
    ro.assignedTechName || getTechName?.(ro.assignedTechId) || "—",
    "Work Stopped",
    "Work stopped."
  );

  push(
    t.waitingPartsAt || ro.waitingPartsAt,
    "System",
    "Waiting for Parts",
    "Moved to waiting for parts."
  );

  push(
    t.completedAt || ro.completedAt,
    ro.assignedTechName || getTechName?.(ro.assignedTechId) || "—",
    "Completed",
    "Marked completed."
  );

  push(t.archivedAt || ro.archivedAt, "System", "Archived", "Archived.");

  push(
    ro.partsActionRequestedAt,
    ro.partsActionRequestedByName || "—",
    "Parts Requested",
    `${String(ro.partsRequestType || "REQUEST").toUpperCase()} — ${
      ro.partsRequestNote || "Parts action requested."
    }`
  );

  push(
    ro.partsActionedAt,
    ro.partsActionedByName || "—",
    "Parts Action Status",
    `Set to ${prettyValue(ro.partsActionStatus || "—")}`
  );

  return items;
}

function getChangedFields(evt) {
  const meta = evt?.meta && typeof evt.meta === "object" ? evt.meta : {};
  return Array.isArray(meta.changedFields) ? meta.changedFields : [];
}

function buildHistoryNote(type, evt, actor) {
  const meta = evt?.meta && typeof evt.meta === "object" ? evt.meta : {};
  const changedFields = getChangedFields(evt);
  const key = safeString(type).trim().toUpperCase();

  if (key === "WORK_STARTED" || key === "RO_WORK_STARTED") {
    return "Work started.";
  }

  if (
    key === "WORK_STOPPED" ||
    key === "WORK_ENDED" ||
    key === "RO_WORK_ENDED"
  ) {
    return "Work stopped.";
  }

  if (
    key === "WORK_COMPLETED" ||
    key === "RO_WORK_COMPLETED" ||
    key === "RO_COMPLETED" ||
    key === "COMPLETED"
  ) {
    const flaggedHours = getMetaNumber(meta, [
      "flaggedHours",
      "flagged_hours",
      "hoursFlagged",
      "hours_flagged",
      "completedHours",
      "completed_hours",
      "hoursCompleted",
      "hours_completed",
      "technicianFlaggedHours",
      "technician_flagged_hours",
    ]);

    if (flaggedHours != null) {
      return `Marked completed. Flagged hours: ${flaggedHours.toFixed(1)}.`;
    }

    return "Marked completed.";
  }

  if (
    key === "RO_ASSIGNED" ||
    key === "TECH_ASSIGNED" ||
    key === "ASSIGNED"
  ) {
    const techName =
      safeString(meta?.techName).trim() ||
      safeString(meta?.to).trim() ||
      safeString(meta?.assignedTechName).trim();

    return techName ? `Assigned to ${techName}.` : "Technician assigned.";
  }

  if (
    key === "RO_UNASSIGNED" ||
    key === "TECH_UNASSIGNED" ||
    key === "UNASSIGNED"
  ) {
    return "Technician unassigned.";
  }

  if (key === "RO_EDITED") {
    if (changedFields.length > 0) {
      const labels = changedFields
        .map((field) => safeString(field?.label).trim())
        .filter(Boolean);

      if (labels.length > 0) {
        return `Edited by ${
          actor && actor !== "—" ? actor : "user"
        } — changed ${labels.join(", ")}.`;
      }
    }

    return actor && actor !== "—" ? `Edited by ${actor}.` : "RO details edited.";
  }

  if (key === "RO_MESSAGE_RETRACTED") {
    return "Withdrew a message.";
  }

  if (key === "RO_ATTACHMENT_RETRACTED") {
    const fileName = safeString(meta?.fileName).trim();
    return fileName ? `Withdrew ${fileName}.` : "Withdrew an attachment.";
  }

  if (meta?.notePreview) return safeString(meta.notePreview);
  if (meta?.techName) return `Technician: ${safeString(meta.techName)}`;

  if (meta?.from && meta?.to) {
    return `Changed from ${prettyValue(meta.from)} to ${prettyValue(meta.to)}.`;
  }

  if (meta?.to) return `Changed to ${prettyValue(meta.to)}.`;

  return "";
}

function buildHistoryEvents(ro) {
  const evts = Array.isArray(ro?.historyEvents) ? ro.historyEvents : [];

  return evts
    .map((evt) => {
      const type = evt?.type || evt?.eventType;
      const rawType = rawEventKey(evt);
      const actor = getActorName(evt, "—");
      const changedFields = getChangedFields(evt);

      return {
        timestamp: getHistoryTimestamp(evt),
        actor,
        type: prettyEventType(type),
        note: buildHistoryNote(type, evt, actor),
        changedFields: shouldHideRawChangedFields(rawType) ? [] : changedFields,
        source: "history",
      };
    })
    .filter((evt) => safeDate(evt.timestamp));
}

function ChangedFieldsList({ fields }) {
  const changedFields = Array.isArray(fields) ? fields : [];
  if (!changedFields.length) return null;

  return (
    <div
      style={{
        marginTop: 6,
        display: "grid",
        gap: 5,
      }}
    >
      {changedFields.map((field, index) => {
        const label = safeString(field?.label || field?.key || "Field").trim();
        const from = prettyValue(field?.from || "—");
        const to = prettyValue(field?.to || "—");

        return (
          <div
            key={`${label}-${index}`}
            style={{
              padding: "5px 7px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#ffffff",
              fontSize: 11,
              color: "#374151",
              display: "grid",
              gap: 1,
            }}
          >
            <div
              style={{
                fontWeight: 900,
                color: "#111827",
                fontSize: 11,
              }}
            >
              {label}
            </div>
            <div>
              <span style={{ color: "#64748b" }}>From:</span>{" "}
              <span style={{ fontWeight: 800 }}>{from}</span>
            </div>
            <div>
              <span style={{ color: "#64748b" }}>To:</span>{" "}
              <span style={{ fontWeight: 800 }}>{to}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ROStatusTimeline({
  ro,
  getTechName,
  title = "History Timeline",
  emptyMessage = "No history available.",
}) {
  const timeline = useMemo(() => {
    if (!ro) return [];

    const systemEvents = buildSystemTimeline(ro, getTechName);
    const historyEvents = buildHistoryEvents(ro);

    const hasHistoryCreated = historyEvents.some(
      (evt) => evt.type === "RO Created"
    );

    /*
      If backend history exists, prefer it over derived timestamp events.

      This prevents duplicate entries like:
      - Work Started from timestamps
      - Work Started from history
      - Work Completed field-change block
      - Completed friendly event

      Keep only the system-created fallback if history does not include creation.
    */
    const createdFallback = systemEvents.filter(
      (evt) => evt.type === "RO Created" && !hasHistoryCreated
    );

    const merged =
      historyEvents.length > 0
        ? [...createdFallback, ...historyEvents]
        : systemEvents;

    const seen = new Set();

    return merged
      .filter((evt) => {
        const key = getEventDedupeKey(evt);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
  }, [ro, getTechName]);

  return (
    <div>
      <div
        style={{
          fontWeight: 900,
          marginBottom: 7,
          fontSize: 15,
          color: "#111827",
        }}
      >
        {title}
      </div>

      {timeline.length === 0 ? (
        <div style={emptyStateStyle()}>{emptyMessage}</div>
      ) : (
        <div style={{ display: "grid", gap: 5 }}>
          {timeline.map((evt, idx) => {
            const tone = getEventTone(evt.type);
            const note = safeString(evt.note).trim();

            return (
              <div
                key={`${evt.type}-${evt.timestamp}-${idx}`}
                style={itemCardStyle(tone)}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    gap: 10,
                    alignItems: "start",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: 13.5,
                      color: "#111827",
                      minWidth: 0,
                    }}
                  >
                    {evt.type}
                  </div>

                  <div
                    style={{
                      fontSize: 11.5,
                      color: "#64748b",
                      whiteSpace: "nowrap",
                      lineHeight: 1.3,
                    }}
                  >
                    {formatWhen(evt.timestamp)}
                  </div>
                </div>

                <div
                  style={{
                    fontSize: 12,
                    marginTop: 4,
                    color: "#374151",
                    lineHeight: 1.35,
                  }}
                >
                  <span style={{ fontWeight: 900, color: "#475569" }}>
                    {evt.actor}:
                  </span>{" "}
                  {note || "—"}
                </div>

                <ChangedFieldsList fields={evt.changedFields} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
